import React, { FC, useRef } from 'react';
import { Field, FieldArrayFieldsProps } from 'redux-form';
import { useTranslations } from '@lang';
import { ReduxFormSelect } from '@common/components/FormElements/BlockSelectField';
import { ReduxFormInput } from '@common/components/FormElements/BlockInputField';
import { maleTitlesOptions, femaleTitlesOptions } from '@utils/guestTitles';
import { RadioGroupField } from '@common/components/RadioGroupField';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { required } from '../validators';

import {
  GuestColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  TitleText,
  GenderRow,
} from './GuestFields.styles';

type GuestFieldProps = {
  guestIndex: number;
  fieldPrefix: string;
  formChange: (...args: any[]) => any;
  fields: FieldArrayFieldsProps<any>;
  showGenderNeutral?: boolean;
};

export const GuestFields: FC<GuestFieldProps> = ({
  fieldPrefix,
  guestIndex,
  formChange,
  fields,
  showGenderNeutral,
}) => {
  const { t } = useTranslations();

  const titleLabel = t('common.title');
  const validateTitle = useRef(required('selectionRequired', titleLabel.toLowerCase()));

  const firstNameLabel = t('common.firstName');
  const validateFirstName = useRef(required('fieldRequired', firstNameLabel));

  const lastNameLabel = t('common.lastName');
  const validateLastName = useRef(required('fieldRequired', lastNameLabel));

  const genderLabel = t('common.gender');
  const validateGender = useRef(required('selectionRequired', genderLabel.toLowerCase()));

  const titlesOptions = [...maleTitlesOptions, ...femaleTitlesOptions].map((option, index) => ({
    ...option,
    label: t(option.label),
    key: index,
  }));
  const isExistingBasketGuest = !!fields?.get(guestIndex)?.id;

  return (
    <div>
      <GuestRow>
        <GuestCounter>
          <TitleText>{t('spa.guestCounter', { counter: guestIndex + 1 })}</TitleText>
        </GuestCounter>
        <InputsRow>
          <GuestColumn>
            <Field
              isFullWidth
              useNativeDropdownOnMobile={false}
              component={ReduxFormSelect}
              labelWidth="50px"
              name={`${fieldPrefix}.title`}
              options={titlesOptions}
              label={titleLabel}
              handleAfterChange={(value) => {
                formChange(`${fieldPrefix}.gender`, value.value === 'Mr' ? 'male' : 'female');
              }}
              parse={(selected) => selected && selected.value}
              format={(selected) => titlesOptions.find(({ value }) => value === selected)}
              id={`${guestIndex}_Guest_title`}
              validate={[validateTitle.current]}
              isDisabled={isExistingBasketGuest}
              isSmall
            />
          </GuestColumn>
          <GuestColumn>
            <Field
              isFullWidth
              component={ReduxFormInput}
              name={`${fieldPrefix}.firstName`}
              label={firstNameLabel}
              placeholder={firstNameLabel}
              id={`${guestIndex}_Guest_fname`}
              validate={[validateFirstName.current]}
              disabled={isExistingBasketGuest}
            />
          </GuestColumn>
          <GuestColumn>
            <Field
              isFullWidth
              component={ReduxFormInput}
              name={`${fieldPrefix}.lastName`}
              label={lastNameLabel}
              placeholder={lastNameLabel}
              id={`${guestIndex}_Guest_lname`}
              validate={[validateLastName.current]}
              disabled={isExistingBasketGuest}
            />
          </GuestColumn>
        </InputsRow>
        <GenderRow>
          <Field
            name={`${fieldPrefix}.gender`}
            component={RadioGroupField}
            cols={3}
            id={`${guestIndex}_Guest_gender`}
            validate={[validateGender.current]}
            label={genderLabel}
            options={showGenderNeutral ? genderOptionsWithNeutral : genderOptions}
            isDisabled={!showGenderNeutral && isExistingBasketGuest}
          />
        </GenderRow>
      </GuestRow>
    </div>
  );
};
